import React, { useState } from "react";
import { Autocomplete, TextField, SearchIcon, parse, match, ListItem } from "../../platform-style";
import { AutocompleteInputChangeReason, Tooltip } from "@mui/material";
import styled from "@emotion/styled";

const SearchIconWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  container-type: inline-size;
`;

type Value = {
  key: string;
  name?: string;
  sentence: string;
};

interface AutocompleteSearchProps {
  id: string;
  name: string;
  label?: string;
  setFieldName: (value: string) => void;
  options: { key: string; name?: string; sentence: string }[];
  selectedOption: any;
  setSelectedOption: (value: any) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  setOffset: (value: any) => void;
  defaultValue?: string;
  placeholder?: string;
  loading?: boolean;
  hasError?: boolean;
  helperText?: string;
  onChange?: (
    event?: React.SyntheticEvent,
    value?: Value | Value[],
    reason?: "createOption" | "selectOption" | "removeOption" | "blur" | "clear"
  ) => void;
  onInputChange?: (
    event?: React.SyntheticEvent,
    value?: string,
    reason?: AutocompleteInputChangeReason
  ) => void;
  disabled?: boolean;
  selectionConstrained?: boolean;
  autoComplete?: boolean;
  autoHighlight?: boolean;
  autoSelect?: boolean;
}

export const AutocompleteSearchBox = (props: AutocompleteSearchProps) => {
  const [tooltipTitle, setTooltipTitle] = useState(props.defaultValue ? props.defaultValue : "");
  const [inputValue, setInputValue] = useState(
    props.name && props.defaultValue ? props.defaultValue : ""
  );

  function handleSearch(event: any, value: string, reason: AutocompleteInputChangeReason) {
    if (!props.selectedOption) {
      props.setOffset(0);
    }
    props.setSearchTerm(event?.target.value);
    props.setFieldName(props.name);

    if (props.onInputChange) props.onInputChange(event, value, reason);
  }

  function handleOptionChange(
    event: any,
    value: any,
    reason: "createOption" | "selectOption" | "removeOption" | "blur" | "clear"
  ) {
    // When freeSolo is enabled, value may be a string
    if (typeof value === "string") {
      setTooltipTitle(value);
      setInputValue(value);
    } else if (value) {
      setTooltipTitle(value.sentence);
      setInputValue(value.sentence);
    } else {
      setTooltipTitle("");
      setInputValue("");
    }

    props.setSearchTerm(event?.target.value);
    props.setSelectedOption(value);
    props.setFieldName(props.name);

    if (props.onChange) props.onChange(event, value, reason);
  }

  return (
    <>
      <Tooltip role={"tooltip"} title={tooltipTitle}>
        <Autocomplete
          freeSolo={!props.selectionConstrained}
          disabled={props.disabled}
          autoComplete={props.autoComplete}
          autoHighlight={props.autoHighlight}
          autoSelect={props.autoSelect}
          noOptionsText="No search results found."
          options={props.options}
          value={inputValue}
          onInputChange={handleSearch}
          isOptionEqualToValue={(option, value) => option === value}
          onChange={handleOptionChange}
          loading={props.loading}
          loadingText="Loading…"
          getOptionLabel={(option) => (typeof option === "string" ? option : option.sentence)}
          renderInput={(params) => (
            <TextField
              {...params}
              id={props.id}
              name={props.name}
              label={props.label ? props.label : ""}
              placeholder={props.placeholder}
              size={"small"}
              error={props.hasError ? props.hasError : false}
              helperText={props.helperText ? props.helperText : ""}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                  ),
                  style: {
                    paddingRight: "39px",
                  },
                },
              }}
              onFocus={(event) => {
                event.target.select();
              }}
            />
          )}
          renderOption={(renderProps, option, { inputValue }) => {
            const matches = match(option.sentence, inputValue, { insideWords: true });
            const parts = parse(option.sentence, matches);

            return (
              <ListItem {...renderProps}>
                <div>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
              </ListItem>
            );
          }}
        />
      </Tooltip>
    </>
  );
};
